import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { IKnmAddressType } from 'APIPackage/CatalogsModule/interfaces/IKnmAddressType';
import { getCatalogKnmAddressTypes } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './AdminEditForm.module.scss';
import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type KnmAddressType = Omit<IKnmAddressType, 'id'>;

interface IKnmAddressTypeEditFormProps {
  onBack: () => void;
}

interface IKnmAddressTypeEditFormStoreProps {
  knmAddressTypes: IKnmAddressType[];
}

const KnmAddressTypeEditFormWithoutStore = ({
  onBack,
  knmAddressTypes,
}: IKnmAddressTypeEditFormProps & IKnmAddressTypeEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmAddressType, setSelectedKnmAddressType] = useState<IKnmAddressType | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmAddressType = knmAddressTypes.find(k => k.id.toString() === id);

      setSelectedKnmAddressType(prev => (prev?.id === knmAddressType?.id ? undefined : knmAddressType));
    },
    [knmAddressTypes],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_ADDRESS_TYPES));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmAddressType({
      id: -1,
      enabled: true,
      code: '',
      name: '',
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmAddressType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmAddressType) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_ADDRESS_TYPES }));
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedKnmAddressType!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.KNM_ADDRESS_TYPES,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmAddressType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmAddressType>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmAddressType> onSubmit={onSubmit} initialValues={selectedKnmAddressType} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Тип места проведения КНМ</span>
        {knmAddressTypes &&
          knmAddressTypes.map((knmInspType: IKnmAddressType) => (
            <button
              key={knmInspType.id}
              className={selectedKnmAddressType?.id === knmInspType.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmInspType.id}
              onClick={onRowClick}
              type="button"
            >
              {knmInspType.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedKnmAddressType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmAddressTypeEditFormStoreProps => ({
  knmAddressTypes: getCatalogKnmAddressTypes(state),
});

export const KnmAddressTypeEditForm = connect(mapStateToProps)(KnmAddressTypeEditFormWithoutStore);
